// Pure rollups for the Inventory Surface's snapshot distributions.
// Operates over the flattened inventory rows (see inventoryRows.js) that
// InventoryBody already loads + scope-filters — no extra fetch. Split out
// (mirroring inventoryRows.js) so the tallies stay node-testable without
// dragging the component import chain into a DOM-less environment.
//
// SNAPSHOT ONLY. The inventory feed is latest-state — one current row per
// endpoint (endpoint_metadata_current is a ReplacingMergeTree), and the
// ENDPOINT analytics entity carries no time field — so these are
// current-state distributions, NOT time series. The platform retains no
// historical inventory to bucket over time; a true "agents installed over
// time" view would need forward-only snapshot retention on the backend.

import { agentDisplayLabel, deriveAgentIdentity } from "./agentIdentity.js";
import { AGENT_COLORS } from "./dimensionRenderers.js";

// House categorical hues for values without a brand color in AGENT_COLORS
// (OS product names, long-tail / unknown agents). Distinct at both chip and
// thin-sliver size. Assigned by descending-count rank so the dominant
// segments get the most separable hues.
const FALLBACK_PALETTE = [
  "#386afe", "#22c55e", "#f59e0b", "#8e44ad", "#ec4899",
  "#06b6d4", "#ef4444", "#84cc16", "#a855f7", "#14b8a6",
];

export function colorForRank(i) {
  return FALLBACK_PALETTE[i % FALLBACK_PALETTE.length];
}

// Mix a hex color toward white by `amount` (0..1). Used to give sibling
// products of one brand (Claude Code / Claude Desktop, both Anthropic blue)
// distinguishable shades instead of two identical adjacent segments.
// Exported for direct unit testing.
export function lighten(hex, amount) {
  const n = parseInt(hex.slice(1), 16);
  const mix = (c) => Math.round(c + (255 - c) * amount);
  const [r, g, b] = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff].map(mix);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

// Assign colors to sorted segments: a segment whose brand has a house color
// gets it, with progressively lighter shades for later siblings of the same
// brand; everything else takes the ranked fallback palette.
function assignSegmentColors(segments) {
  const brandUses = new Map();
  return segments.map((s, i) => {
    const brandKey = deriveAgentIdentity(s.key).key;
    const brand = AGENT_COLORS[brandKey];
    if (!brand) return { ...s, color: colorForRank(i) };
    const uses = brandUses.get(brandKey) ?? 0;
    brandUses.set(brandKey, uses + 1);
    return { ...s, color: uses === 0 ? brand : lighten(brand, Math.min(0.25 * uses, 0.6)) };
  });
}

// AI-agent distribution. Unit = agent PRESENCE per endpoint: an endpoint is
// counted once per distinct installed agent it reports (a duplicate report
// of the same agent on one endpoint counts once), so the bar partitions
// "endpoints running each agent" across the fleet. Multi-agent endpoints
// contribute to several segments, so segment counts sum to >= endpoint count
// — that sum is the bar's denominator.
//
// Segments are PRODUCT-level: "Claude Code" and "Claude Desktop" are
// different agents and get their own segments (they share the brand color in
// different shades). Rows from endpoints that predate the agent-name rekey
// still carry legacy enum values ("Claude") and appear as their own shrinking
// segment until the endpoint re-reports.
export function agentDistribution(rows) {
  const list = Array.isArray(rows) ? rows : [];
  // Keyed by RENDERED LABEL. See agentDistributionFromGroups below for why.
  // Two raw names that display identically must be one segment.
  const byLabel = new Map(); // rendered label -> { key, label, count }
  let endpointsWithAgents = 0;

  for (const row of list) {
    const installed = Array.isArray(row?.installedAiAgentsList) ? row.installedAiAgentsList : [];
    const labelsHere = new Map(); // label -> a raw name that produced it
    for (const a of installed) {
      const name = typeof a?.name === "string" ? a.name.trim() : "";
      if (!name) continue;
      const label = agentDisplayLabel(name);
      if (!labelsHere.has(label)) labelsHere.set(label, name);
      if (!byLabel.has(label)) {
        byLabel.set(label, { key: name, label, count: 0 });
      }
    }
    if (labelsHere.size > 0) endpointsWithAgents++;
    for (const label of labelsHere.keys()) {
      byLabel.get(label).count++;
    }
  }

  const total = [...byLabel.values()].reduce((n, s) => n + s.count, 0);
  const segments = assignSegmentColors(
    [...byLabel.values()]
      .sort((a, b) => b.count - a.count)
      .map((s) => ({
        key: s.key,
        label: s.label,
        count: s.count,
        share: total ? s.count / total : 0,
      })),
  );

  return {
    segments,
    total,
    endpointsTotal: list.length,
    endpointsWithAgents,
  };
}

// AI-agent distribution from SERVER-aggregated counts. Same segment shape and
// semantics as agentDistribution() above, but the O(fleet) counting happened in
// ClickHouse (see apiClient.getInventoryAgentDistribution) so it scales past the
// client-side row cap. `groups` = [{ value: agentName, count: nEndpoints }]
// (distinct endpoints per installed agent from AGENT_INVENTORY);
// `endpointsTotal` is the distinct endpoint count resolved by the server.
// One segment per agent name - no brand folding (see the
// product-level note on agentDistribution) — so the server's distinct-endpoint
// counts carry through untouched; the old fold-and-sum fidelity gap is gone.
export function agentDistributionFromGroups(groups, { endpointsTotal = 0 } = {}) {
  const list = Array.isArray(groups) ? groups : [];
  // Keyed by the RENDERED LABEL, not the raw name. Two raw names that display
  // identically are the same product to a reader, and rendering them as two
  // segments with the same text and different counts is unreadable: the legacy
  // enum `Openclaw` and the reported product name `OpenClaw` both label as
  // "OpenClaw". Unlike the per-endpoint list in inventoryRows.js, these groups
  // carry no install path, so there is nothing that could distinguish them:
  // folding is the only correct presentation. `key` keeps the first raw name
  // seen so brand colour resolution is unchanged.
  const byLabel = new Map(); // rendered label -> { key, label, count }

  for (const g of list) {
    const name = typeof g?.value === "string" ? g.value.trim() : "";
    if (!name) continue;
    const label = agentDisplayLabel(name);
    const cur = byLabel.get(label) || { key: name, label, count: 0 };
    cur.count += Number(g?.count) || 0;
    byLabel.set(label, cur);
  }

  const total = [...byLabel.values()].reduce((n, s) => n + s.count, 0);
  const segments = assignSegmentColors(
    [...byLabel.values()]
      .sort((a, b) => b.count - a.count)
      .map((s) => ({
        key: s.key,
        label: s.label,
        count: s.count,
        share: total ? s.count / total : 0,
      })),
  );

  return {
    segments,
    total,
    endpointsTotal: Number(endpointsTotal) || 0,
  };
}

// OS distribution. Unit = endpoints: each endpoint has exactly one resolved
// `platform` (the build-corrected product name the table's "Operating system"
// column shows, e.g. "Windows 11 Pro"), so segments partition the endpoint
// set cleanly to 100%. Empty platform falls back to "Unknown".
export function osDistribution(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const byLabel = new Map();

  for (const row of list) {
    const label = (row?.platform && String(row.platform).trim()) || "Unknown";
    if (!byLabel.has(label)) byLabel.set(label, { key: label, label, count: 0 });
    byLabel.get(label).count++;
  }

  const total = list.length;
  const segments = [...byLabel.values()]
    .sort((a, b) => b.count - a.count)
    .map((s, i) => ({
      key: s.key,
      label: s.label,
      count: s.count,
      share: total ? s.count / total : 0,
      color: colorForRank(i),
    }));

  return { segments, total, endpointsTotal: total };
}

// OS distribution from SERVER-aggregated counts. Same segment shape and 100%
// partition as osDistribution() above, but counted in ClickHouse (see
// apiClient.getInventoryOsDistribution) so it scales past the client row cap.
// `groups` = [{ product, major, count }] — distinct endpoints per
// (os_version.product_name, os_version.major) cell, using only dimensions that
// already exist in the graph (no backend change). The OS family is inferred from
// the proto field contract for those two fields:
//   • product_name present  → Windows (it's Windows-only); use it as the label,
//     so Windows keeps build granularity ("Windows 11 Pro", …).
//   • else major present     → macOS (major carries the kernel major on macOS,
//     and is absent on Linux) → coarse "macOS".
//   • else                   → "Other" (no product + no major: Linux, or an
//     older agent that reported neither).
// This is coarser than the table's `row.platform` (which has the real Platform
// enum from the REST read path) for the non-Windows split, but needs no new
// dimension. Cells are scalar per endpoint, so segment counts sum to the total.
export function osDistributionFromGroups(groups) {
  const list = Array.isArray(groups) ? groups : [];
  const byLabel = new Map();

  for (const g of list) {
    const product = (g?.product && String(g.product).trim()) || "";
    const major = (g?.major && String(g.major).trim()) || "";
    const label = product && product !== "Unknown"
      ? product            // product_name ⇒ Windows (build-granular)
      : major
        ? "macOS"          // no product, has kernel major ⇒ macOS
        : "Other";         // neither ⇒ Linux / unreported
    const cur = byLabel.get(label) || { key: label, label, count: 0 };
    cur.count += Number(g?.count) || 0;
    byLabel.set(label, cur);
  }

  const total = [...byLabel.values()].reduce((n, s) => n + s.count, 0);
  const segments = [...byLabel.values()]
    .sort((a, b) => b.count - a.count)
    .map((s, i) => ({
      key: s.key,
      label: s.label,
      count: s.count,
      share: total ? s.count / total : 0,
      color: colorForRank(i),
    }));

  return { segments, total, endpointsTotal: total };
}
